import React, { createContext, useContext, useState, useEffect } from 'react';
import currencyService from '../services/currencyService';
import merchantService from '../services/merchantService';
import { useMerchant } from './MerchantContext';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Convenience hook for currency formatting
export const useCurrencyFormatter = () => {
  const { formatAmount, currency, getCurrencySymbol } = useCurrency();
  return {
    formatAmount,
    formatCurrency: formatAmount, // Alias for backward compatibility
    currency,
    symbol: getCurrencySymbol(),
    getCurrencySymbol
  };
};

export const CurrencyProvider = ({ children }) => {
  const merchantContext = useMerchant();
  const currentMerchant = merchantContext?.currentMerchant;

  const [currency, setCurrency] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load available currencies
  const loadCurrencies = async () => {
    try {
      const response = await currencyService.getCurrencies();
      // Handle different response structures
      const currenciesData = response.data || response.results || response;

      if (Array.isArray(currenciesData)) {
        setCurrencies(currenciesData);
      } else {
        console.warn('Currencies API returned non-array data:', currenciesData);
        setCurrencies(currencyService.getPopularCurrencies());
      }
    } catch (error) {
      console.error('Error loading currencies:', error);
      // Fallback to popular currencies if API fails
      setCurrencies(currencyService.getPopularCurrencies());
    }
  };

  // Load merchant currency
  const loadMerchantCurrency = async () => {
    try {
      const merchant = await merchantService.getCurrentMerchant();
      if (merchant?.settings?.currency) {
        setCurrency(merchant.settings.currency);
      } else {
        // Try to detect currency if merchant doesn't have one set
        await detectAndSetCurrency();
      }
    } catch (error) {
      console.error('Error loading merchant currency:', error);
      // Try to detect currency as fallback
      await detectAndSetCurrency();
    }
  };

  // Detect currency based on user location
  const detectAndSetCurrency = async () => {
    try {
      const detectedCurrency = await currencyService.detectCurrency();
      setCurrency(detectedCurrency);
    } catch (error) {
      console.error('Error detecting currency:', error);
      // Fallback to PHP
      setCurrency({
        code: 'PHP',
        name: 'Philippine Peso',
        symbol: '₱',
        decimal_places: 2
      });
    }
  };

  // Change currency
  const changeCurrency = async (newCurrency) => {
    try {
      setLoading(true);

      // Update merchant settings with new currency
      const merchant = await merchantService.getCurrentMerchant();
      if (merchant) {
        // Send only the currency code, not the entire object
        const currencyValue = newCurrency.code;
        await merchantService.updateMerchantSettings(merchant.id, {
          currency: currencyValue
        });
      }

      setCurrency(newCurrency);
      setError(null);
    } catch (error) {
      console.error('Error changing currency:', error);
      setError('Failed to change currency. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Format currency amount
  const formatAmount = (amount, options = {}) => {
    if (!currency) return amount?.toString() || '0';

    if (options.symbolOnly) {
      return currencyService.formatCurrencySymbol(amount, currency);
    }

    return currencyService.formatCurrency(amount, currency);
  };

  // Get currency symbol
  const getCurrencySymbol = () => {
    return currencyService.getCurrencySymbol(currency);
  };

  // Validate amount
  const validateAmount = (amount) => {
    return currencyService.validateAmount(amount, currency);
  };

  // Convert to display format
  const toDisplayAmount = (amount) => {
    return currencyService.toDisplayAmount(amount, currency);
  };

  // Parse display amount
  const parseDisplayAmount = (displayAmount) => {
    return currencyService.parseDisplayAmount(displayAmount);
  };

  // Initialize currency data
  useEffect(() => {
    const initializeCurrency = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadCurrencies(),
          loadMerchantCurrency()
        ]);
      } catch (error) {
        console.error('Error initializing currency:', error);
        setError('Failed to load currency data');
      } finally {
        setLoading(false);
      }
    };

    initializeCurrency();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload currency when merchant changes
  useEffect(() => {
    if (currentMerchant) {
      console.log('💱 CurrencyContext: Merchant changed, reloading currency for:', currentMerchant.name);
      loadMerchantCurrency();
    }
  }, [currentMerchant?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    // State
    currency,
    currencies,
    loading,
    error,

    // Actions
    changeCurrency,
    detectAndSetCurrency,
    loadCurrencies,
    loadMerchantCurrency,

    // Utilities
    formatAmount,
    getCurrencySymbol,
    validateAmount,
    toDisplayAmount,
    parseDisplayAmount,

    // Clear error
    clearError: () => setError(null)
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
